/**
 * The "Only mock-ups" page's model: mockup-only jobs grouped into the Generate runs that made
 * them, and an export of chosen runs as files plus the Mock-ups sheet — one row per run.
 */
import type { GenerationJobView, PhoneModelOption } from "@genz/platform";
import { ALL_VIEWS } from "../templates/views.js";
import { PRODUCT_CATEGORIES } from "../templates/catalogOptions.js";
import { groupRuns, phoneName, viewLabel, type Run, type RunDetails } from "../generate/runs.js";
import { modelLabel } from "../templates/templateLabels.js";
import { wizardView, type MockupWizardDetails } from "../stores/wizard.js";
import { slugify } from "../export/refs.js";
import type { SheetRowInput } from "../export/sheet.js";

export type MockupRunDetails = RunDetails;
export type MockupRun = Run;
export { groupRuns, viewLabel };

export type ImageExt = "png" | "jpg" | "webp";

export function runLabel(run: MockupRun): string {
  const { name, brand, model } = run.details;
  return name?.trim() || phoneName(brand, model) || "Mockup";
}

/** "Make template": the wizard's Details from the run, for the one view the template is made from.
 *  An older run has no catalog link, so its brand and model words find the phone model. */
export function templateDetails(run: MockupRun, job: GenerationJobView, models: PhoneModelOption[]): MockupWizardDetails {
  const d = run.details;
  const same = (a?: string | null, b?: string | null) => (a ?? "").toLowerCase() === (b ?? "").toLowerCase();
  const model = d.phoneModelId
    ? models.find((m) => m.id === d.phoneModelId)
    : models.find((m) => same(m.brand, d.brand) && same(m.model, d.model));
  return {
    category: d.productType || PRODUCT_CATEGORIES[0].value,
    brand: model?.brand ?? d.brand ?? "",
    phoneModelId: model?.id ?? d.phoneModelId ?? "",
    caseStyleId: d.caseStyleId ?? "",
    baseType: d.baseType ?? "",
    code: d.name?.trim() ?? "",
    templateName: model ? modelLabel(model) : "",
    views: [wizardView(job.view)],
  };
}

export function extOf(mediaType: string): ImageExt {
  if (mediaType === "image/jpeg") return "jpg";
  return mediaType === "image/webp" ? "webp" : "png";
}

/** A file-system-safe, readable name that no earlier file of this export has taken. */
function uniqueName(base: string, ext: string, taken: Set<string>): string {
  const safe = base.replace(/[\\/:*?"<>|]+/g, "-").trim() || "Mockup";
  for (let n = 1; ; n++) {
    const name = `${safe}${n > 1 ? ` (${n})` : ""}.${ext}`;
    if (!taken.has(name.toLowerCase())) {
      taken.add(name.toLowerCase());
      return name;
    }
  }
}

export interface RunImages {
  run: MockupRun;
  images: { job: GenerationJobView; bytes: Uint8Array; ext: ImageExt }[];
}

/** The files to save and the sheet's rows: a run's images fill View 1..N of its one row, in
 *  view order, and the print-file cell stays empty because a mock-up has no print file. Every
 *  image is saved; `notInSheet` counts those past the sheet's view columns. */
export function exportPlan(
  chosen: RunImages[],
  caseStyleLabel: (id: string | null) => string,
): { files: { relPath: string; bytes: Uint8Array }[]; rows: SheetRowInput[]; notInSheet: number } {
  const taken = new Set<string>();
  const files: { relPath: string; bytes: Uint8Array }[] = [];
  const rows: SheetRowInput[] = [];
  let notInSheet = 0;
  for (const { run, images } of chosen) {
    const label = runLabel(run);
    const paths = images.map((image) => {
      const relPath = uniqueName(`${label} - ${viewLabel(image.job.view)}`, image.ext, taken);
      files.push({ relPath, bytes: image.bytes });
      return relPath;
    });
    const slots = paths.slice(0, ALL_VIEWS.length).map((relPath, i) => [ALL_VIEWS[i], relPath] as const);
    notInSheet += paths.length - slots.length;
    const { brand, model, name } = run.details;
    const style = caseStyleLabel(run.details.caseStyleId);
    rows.push({
      customer: "", product: "",
      phoneModel: phoneName(brand, model),
      caseStyle: style,
      phoneSlug: model ? slugify(model) : "",
      caseStyleSlug: style ? slugify(style) : "",
      designName: label,
      designSlug: slugify(name || label),
      ext: images[0]?.ext ?? "png",
      generatedViews: slots.map(([slot]) => slot),
      printFile: false,
      viewRefs: Object.fromEntries(slots),
    });
  }
  return { files, rows, notInSheet };
}
